<template>
    <div>
        <b-table
            hover
            small
            responsive
            borderless
            :items="initiatives"
            :fields="fields"
            @head-clicked="onClickSortHandler"
            sort-by="due_date"
            sort-asc="true"
            show-empty
            :empty-text="emptyText"
            :key="tableKey"
        >
            <template #head(title)="{ label, column }">
                <th-with-sort :label="label" :sortDirection="sortedBy[column]" />
            </template>
            <template #head(status)="{ label, column }">
                <th-with-sort :label="label" :sortDirection="sortedBy[column]" />
            </template>
            <template #head(due_date)="{ label, column }">
                <th-with-sort :label="label" :sortDirection="sortedBy[column]" />
            </template>
            <template #head(edit_delete)> </template>

            <template #cell(title)="row">
                <div class="border-start pt-1 mb-2">
                    <span>{{ row.item.title }}</span>
                    <br />
                    <template v-if="showLinkKr">
                        <div v-if="row.item.key_result">
                            <span class="text-muted">{{ row.item.key_result.title }}</span>
                        </div>
                        <div v-else>
                            <b-link
                                id="btn-daily-profile-initiatives-link-key-result"
                                class="text-primary"
                                @click="onClickLinkKR(row.item)"
                                :disabled="!row.item.has_authorization"
                            >
                                <img :src="Link" height="8" class="mr-2" alt="link icon" />
                                {{ $t('linkKeyResult') }}
                            </b-link>
                        </div>
                    </template>
                </div>
            </template>
            <template #cell(status)="row">
                <b-dropdown
                    :id="`dropdown-daily-initiatives-profile-change-status-${row.item.id}`"
                    toggle-class="dropdown-daily-initiatives-profile-change-status"
                    class="status pt-2"
                    :variant="getColorByStatus(row.item.status)"
                    boundary="viewport"
                    :disabled="!row.item.has_authorization"
                >
                    <template slot="button-content">
                        <span class="font-weight-bold">{{ getSelectedStatus(row.item.status) }}</span>
                    </template>
                    <b-dropdown-item v-for="item in options" :key="item.value" @click="onClickStatus(row.item, item)">{{
                        item.text
                    }}</b-dropdown-item>
                    <b-tooltip
                        :target="`dropdown-daily-initiatives-profile-change-status-${row.item.id}`"
                        v-if="!row.item.has_authorization"
                    >
                        {{ $t('notAuthorizedToChangeStatus') }}
                    </b-tooltip>
                </b-dropdown>
            </template>
            <template #cell(due_date)="row">
                <div class="pt-3">
                    <span v-if="row.item.due_date">
                        {{ row.item.due_date | formatDate }}
                    </span>
                    <span class="pl-4" v-else> - </span>
                </div>
            </template>
            <template #cell(edit_delete)="row">
                <Actions :key="detailsKey" :initiativeId="row.item.id" :disabled="!row.item.has_authorization" />
                <EditInitiativeModal :key="detailsKey + 1" :initiativeId="row.item.id" />
            </template>

            <template #empty="scope">
                <b-alert show class="m-0 mb-3">
                    <span>{{ scope.emptyText }}</span>
                </b-alert>
            </template>
        </b-table>
        <div
            id="btn-daily-initiatives-profile-new-initiative"
            class="border-start pt-1"
            @click="$bvModal.show('new-initiative-modal')"
        >
            <img src="@/assets/icons/create-initiative.svg" />
            {{
                $t('form.create', {
                    item: $t('daily.initiative'),
                })
            }}
        </div>

        <LinkKeyResult
            v-if="showLinkKr"
            @openModal="openModalCallback = $event"
            @keyResultLinked="getInitiativesFromAPI"
        />
    </div>
</template>

<script>
import Vue from 'vue'
import i18n from '@/i18n'
import api from '@/services/api'
import { getDateWithTime } from '@/lib/date'
import { success, danger } from '@/lib/notification'
import Link from '@/assets/icons/link.svg'
import LinkKeyResult from '../link-key-result'
import { Actions } from './components'
import { mapState, mapActions } from 'vuex'

Vue.component('th-with-sort', {
    props: ['label', 'sortDirection'],

    computed: {
        sortIcon() {
            return this.sortDirection ? `fa-sort-${this.sortDirection}` : 'fa-sort'
        },
    },
    template: `
        <div class="c-th">
            <span class="mr-1">
                {{ label }}
            </span>
            <i :class="['fa', sortIcon]"></i>
        </div>
    `,
})

export default {
    name: 'InitiativesTable',
    components: { LinkKeyResult, Actions, EditInitiativeModal: () => import('@/views/okr/initiatives/edit/Edit.vue') },
    props: {
        initiatives: {
            type: Array,
            required: true,
        },
        emptyText: String,
    },
    data() {
        return {
            tableKey: 0,
            detailsKey: 0,
            Link,
            sortedBy: 'due_date',
            fields: [
                {
                    key: 'title',
                    label: i18n.t('daily.initiative'),
                    class: 'dark-blue',
                    sortable: true,
                },
                {
                    key: 'status',
                    label: i18n.t('daily.status'),
                    class: 'dark-blue',
                    sortable: true,
                },
                {
                    key: 'due_date',
                    label: i18n.t('daily.dueDate'),
                    class: 'dark-blue',
                    sortable: true,
                },
                { key: 'edit_delete', label: '', sortable: false, class: 'actions' },
            ],
            options: [
                { value: 'todo', text: i18n.t('daily.statusType.notStarted') },
                { value: 'doing', text: i18n.t('daily.statusType.inProgress') },
                { value: 'done', text: i18n.t('daily.statusType.completed') },
            ],
            openModalCallback: () => {},
        }
    },
    computed: {
        ...mapState('profile', ['user']),
        showLinkKr() {
            return this.user.has_modules.includes('has_okr')
        },
    },
    watch: {
        initiatives() {
            this.tableKey++
        },
    },
    filters: {
        formatDate: function (value) {
            return getDateWithTime(value, 'DD/MM/YYYY')
        },
    },
    methods: {
        ...mapActions('daily', ['getInitiativesFromAPI']),
        getSelectedStatus(value) {
            return this.options.find((opt) => opt.value == value).text
        },
        onClickStatus(initiative, item) {
            if (initiative.status != item.value) {
                initiative.status = item.value
                api.put(`/initiatives/${initiative.id}`, initiative)
                    .then(() => {
                        this.notificationUpdatedSuccessfully()
                        this.$store.dispatch('getOKRsFromAPI')
                    })
                    .catch((e) => {
                        console.log(e)
                        this.notificationError()
                    })
            }
        },
        notificationUpdatedSuccessfully() {
            success(
                i18n.t('notification.updatedSuccessfully', {
                    item: i18n.t('daily.initiative'),
                })
            )
        },
        notificationError() {
            danger()
        },
        getColorByStatus(status) {
            switch (status) {
                case 'done':
                    return 'success'
                case 'doing':
                    return 'warning'
                case 'todo':
                    return 'danger'
            }
        },
        onClickSortHandler(columnName) {
            this.sortedBy = {
                ...this.sortedBy,
                [columnName]: this.sortedBy[columnName] === 'desc' ? 'asc' : 'desc',
                column: columnName,
            }
            this.detailsKey++
        },
        onClickLinkKR(initiative) {
            this.openModalCallback(initiative)
        },
    },

    i18n: {
        messages: {
            'en-US': {
                linkKeyResult: 'Link to a key result',
                notAuthorizedToChangeStatus: 'You are not authorized to change the status of this Initiative',
            },
            'es-ES': {
                linkKeyResult: 'Enlace a un resultado clave',
                notAuthorizedToChangeStatus: 'No está autorizado a cambiar el estado de esta Iniciativa',
            },
            'pt-BR': {
                linkKeyResult: 'Vincular a um resultado chave',
                notAuthorizedToChangeStatus: 'Você não tem autorização para alterar o status desta Iniciativa',
            },
        },
    },
}
</script>

<style scoped>
.dark-blue {
    color: #022b4f;
}
.border-start {
    max-width: 70%;
    border-left: 4px solid #ccebf8 !important;
    padding-left: 1rem !important;
}
.status {
    min-width: 8rem;
    background-color: none;
}
.dropdown-toggle::after {
    float: right;
    margin-top: 0.5rem;
}

.table {
    margin-bottom: 0;
}

.table.b-table > thead > tr > th[aria-sort]::after,
.table.b-table > tfoot > tr > th[aria-sort]::after {
    color: white;
}

::v-deep td:not(:first-child) {
    padding-right: 1rem;
    padding-left: 1rem;
}

::v-deep td:first-child,
::v-deep th:first-child {
    padding-left: 0;
}

::v-deep th:not(:first-child),
::v-deep td:not(:first-child) {
    text-align: center;
}

::v-deep td.actions {
    vertical-align: middle !important;
}

::v-deep th {
    background-image: none !important;
}

#btn-daily-initiatives-profile-new-initiative {
    display: flex;
    gap: 0.5rem;
    align-content: center;
    color: #009ada;
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-size: 0.9rem;
    padding-top: 0.6rem !important;
    padding-bottom: 0.6rem;
    cursor: pointer;
}

.empty-key-results {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    vertical-align: middle;
    color: #444444;
}
</style>
